import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Label, Switch } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BranchTable } from './components';
import type { Branch } from './components/branchesMeta';

type BranchListPageViewProps = {
    canImportProducts: (branch: Branch) => boolean;
    data: Branch[];
    includeArchived: boolean;
    onRequestArchive: (id: string) => void;
    onSetArchived: (id: string, archived: boolean) => void;
    onToggleArchived: (next: boolean) => void;
    onAddBranch: () => void;
    onDeleteBranch: (id: string) => void;
    onEditBranch: (id: string) => void;
    onImportProducts: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSearchChange: (value: string) => void;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

export const BranchListPageView: React.FC<BranchListPageViewProps> = ({
    canImportProducts,
    data,
    includeArchived,
    onRequestArchive,
    onSetArchived,
    onToggleArchived,
    onAddBranch,
    onDeleteBranch,
    onEditBranch,
    onImportProducts,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    page,
    pageSize,
    search,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t(
                'branches.components.branchTable.searchPlaceholder',
            ),
            searchAria: t('branches.components.branchTable.searchAria'),
            showArchived: t('branches.components.branchTable.showArchived'),
            addBranch: t('branches.components.branchTable.addBranch'),
            tableAria: t('branches.components.branchTable.tableAria'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tableAria}
            {...getNodeTestIdProp('branch-table-section')}
        >
            <div
                className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('branch-table-toolbar')}
            >
                <label
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto sm:shrink-0"
                    htmlFor="branch-table-search"
                    {...getNodeTestIdProp('branch-table-search-wrap')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:magnifier"
                    />
                    <input
                        aria-label={copy.searchAria}
                        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={copy.searchPlaceholder}
                        type="text"
                        value={search}
                        id="branch-table-search"
                        {...getNodeTestIdProp('branch-table-search')}
                    />
                </label>
                <div className="flex items-center gap-3">
                    <Switch
                        isSelected={includeArchived}
                        onChange={onToggleArchived}
                        {...getNodeTestIdProp('branch-table-show-archived')}
                    >
                        <Label className="text-sm text-muted">
                            {copy.showArchived}
                        </Label>
                    </Switch>
                    <Button
                        onPress={onAddBranch}
                        variant="primary"
                        {...getNodeTestIdProp('branch-table-add')}
                    >
                        <Icon icon="gravity-ui:plus" />
                        {copy.addBranch}
                    </Button>
                </div>
            </div>
            <BranchTable
                canImportProducts={canImportProducts}
                data={data}
                onDeleteBranch={onDeleteBranch}
                onImportProducts={onImportProducts}
                onRequestArchive={onRequestArchive}
                onSetArchived={onSetArchived}
                onEditBranch={onEditBranch}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
            />
        </section>
    );
};
